<template>
  <div class="row g-3">
    <div class="col-lg-4">
      <div class="card h-100">
        <div class="card-header">
          <h6 class="mb-0">{{ title }}</h6>
          <span v-if="subtitle" class="text-muted f-12">{{ subtitle }}</span>
        </div>
        <div class="card-body">
          <div v-if="!canAdd" class="alert alert-light mb-0" role="alert">
            You do not have permission to add {{ title.toLowerCase() }}.
          </div>
          <form v-else @submit.prevent="submitAdd">
            <div v-if="formError" class="alert alert-danger" role="alert">
              {{ formError }}
            </div>
            <div class="form-group form-field mb-2">
              <label class="form-label" :for="inputId">{{ fieldLabel }}</label>
              <input
                :id="inputId"
                v-model="formValue"
                class="form-control"
                type="text"
                maxlength="100"
                :placeholder="fieldPlaceholder"
                :disabled="saving"
              >
            </div>
            <button type="submit" class="btn btn-primary w-100" :disabled="saving || !formValue.trim()">
              <vue-feather type="plus" class="me-1" />
              {{ saving ? 'Adding...' : addButtonLabel }}
            </button>
          </form>
        </div>
      </div>
    </div>

    <div class="col-lg-8">
      <div class="card h-100">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="mb-0">{{ listTitle }}</h6>
          <button
            class="btn btn-outline-primary btn-sm"
            type="button"
            :disabled="loading"
            @click="loadItems"
          >
            <vue-feather type="refresh-cw" class="me-1" />
            Refresh
          </button>
        </div>
        <div class="card-body">
          <div v-if="error" class="alert alert-danger" role="alert">
            {{ error }}
          </div>
          <div v-else-if="loading" class="text-center py-4 text-muted">
            Loading...
          </div>
          <div v-else-if="items.length === 0" class="text-center py-4 text-muted">
            No records found.
          </div>
          <div v-else class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{{ fieldLabel }}</th>
                  <th v-if="showDeletedColumn">Status</th>
                  <th class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in items" :key="item[idField]">
                  <td>{{ item[idField] }}</td>
                  <td><strong>{{ item[labelField] }}</strong></td>
                  <td v-if="showDeletedColumn">
                    <span
                      class="badge"
                      :class="item.deleted ? 'badge-danger' : 'badge-success'"
                    >
                      {{ item.deleted ? 'Deleted' : 'Active' }}
                    </span>
                  </td>
                  <td class="text-end">
                    <button
                      v-if="canDelete"
                      class="btn btn-sm btn-outline-danger"
                      type="button"
                      title="Remove"
                      @click="confirmRemove(item)"
                    >
                      <vue-feather type="trash-2" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="itemToRemove"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Remove {{ title }}</h5>
              <button type="button" class="btn-close" aria-label="Close" @click="itemToRemove = null" />
            </div>
            <div class="modal-body">
              <p class="mb-2">
                Remove <strong>{{ itemToRemove[labelField] }}</strong>?
              </p>
              <p class="text-muted mb-0 f-12">
                {{ deleteMessage }}
              </p>
              <div v-if="actionError" class="alert alert-danger mt-3 mb-0" role="alert">
                {{ actionError }}
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="itemToRemove = null">
                Cancel
              </button>
              <button type="button" class="btn btn-danger" :disabled="actionLoading" @click="submitRemove">
                {{ actionLoading ? 'Removing...' : 'Remove' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="itemToRemove" class="modal-backdrop fade show" @click="itemToRemove = null" />
    </teleport>
  </div>
</template>

<script>
export default {
  props: {
    active: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      required: true,
    },
    listTitle: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    fieldLabel: {
      type: String,
      required: true,
    },
    fieldPlaceholder: {
      type: String,
      default: '',
    },
    fieldName: {
      type: String,
      required: true,
    },
    idField: {
      type: String,
      required: true,
    },
    labelField: {
      type: String,
      required: true,
    },
    addButtonLabel: {
      type: String,
      default: 'Add',
    },
    deleteMessage: {
      type: String,
      required: true,
    },
    showDeletedColumn: {
      type: Boolean,
      default: false,
    },
    canAdd: {
      type: Boolean,
      default: true,
    },
    canDelete: {
      type: Boolean,
      default: true,
    },
    listFn: {
      type: Function,
      required: true,
    },
    addFn: {
      type: Function,
      required: true,
    },
    removeFn: {
      type: Function,
      required: true,
    },
  },

  data() {
    return {
      items: [],
      formValue: '',
      loading: false,
      error: '',
      formError: '',
      saving: false,
      itemToRemove: null,
      actionError: '',
      actionLoading: false,
      loaded: false,
    }
  },

  computed: {
    inputId() {
      return `settings-${this.fieldName}`
    },
  },

  watch: {
    active: {
      immediate: true,
      handler(isActive) {
        if (isActive && !this.loaded) {
          this.loadItems()
        }
      },
    },
  },

  methods: {
    async loadItems() {
      this.loading = true
      this.error = ''

      try {
        const data = await this.listFn()
        this.items = Array.isArray(data) ? data : []
        this.loaded = true
      } catch (err) {
        if (err.status === 403) {
          this.error = `You do not have permission to view ${this.title.toLowerCase()}.`
        } else {
          this.error = err.message || 'Failed to load records'
        }
      } finally {
        this.loading = false
      }
    },

    async submitAdd() {
      const value = this.formValue.trim()
      if (!value) return

      this.formError = ''
      this.saving = true

      try {
        await this.addFn(value)
        this.formValue = ''
        await this.loadItems()
      } catch (err) {
        this.formError = err.message || 'Failed to add record'
      } finally {
        this.saving = false
      }
    },

    confirmRemove(item) {
      this.itemToRemove = item
      this.actionError = ''
    },

    async submitRemove() {
      this.actionError = ''
      this.actionLoading = true

      try {
        await this.removeFn(this.itemToRemove[this.idField])
        this.itemToRemove = null
        await this.loadItems()
      } catch (err) {
        this.actionError = err.message || 'Failed to remove record'
      } finally {
        this.actionLoading = false
      }
    },
  },
}
</script>
